import React, {useContext, useRef} from 'react';
import {classes, style} from './doppe-action-type-widget-item-list.st.css';
import {CommonClasses, FlexLayout} from '@wix/devzai-common-client';
import {
    IconButton,
    ListView,
    ListViewDataSourceDataItem,
    ListViewItemRoot,
    NavigationOrientation,
    reactCreateMemo,
    ScrollView,
    ScrollViewEdgeDisabled,
    ScrollViewEdgeDisabledSide,
    ScrollViewScrollbars,
    ScrollViewScrollingDirection,
    Swiper,
    SwiperNavigationListView,
    useDynamicRef,
    useSwiperNavigation,
    useWindowDependantMemo
} from '@wix/devzai-utils-react';
import IconChevronLeft
    from '../../../components/doppe-action-button-templates/doppe-standard-action-button-template/icons/ChevronLeft.svg';
import IconChevronRight
    from '../../../components/doppe-action-button-templates/doppe-standard-action-button-template/icons/ChevronRight.svg';
import {domGetScrollbarSize} from '@wix/devzai-utils-dom';
import {IDoppeViewerActionContext, useBymoPageContextProps} from '../../../doppe-sdk';
import {
    arraySkipTake,
    enumIsEnumValue,
    evaluateFunction,
    HtmlObjectResizeMode,
    OmitStrict,
    Values
} from '@wix/devzai-utils-common';
import {
    BymoPageActionMediaViewLayout,
    BymoPageActionMediaViewSettings,
    DoppeActionLayout,
    DoppeHideableValue,
    doppeHideableValueCreateHidden,
    doppeHideableValueIsVisibleAndNotEqualValue,
    doppePremiumFeatureGetLimit,
    DoppePremiumFeatures
} from '../../../client-server-common';
import {WixMediaResource} from '@wix/devzai-common-wix';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetLayoutContext
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {
    useDoppViewerIsRtl
} from '../../../components/doppe-viewer-bymo-page-props-react/doppe-viewer-bymo-page-props-react';
import {DoppePageActionMediaView} from '../../../components/doppe-page-action-media-view/doppe-page-action-media-view';
import {DoppePageMediaView} from '../../../components/doppe-page-media-view/doppe-page-media-view';
import {
    doppeActionCtaButtonsSettingsHasVisibleButton
} from '../../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
} from '../../../components/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons';

export const DoppeActionTypeWidgetItemListPredefinedMediaViewSettings = {
    OutfitMediaSettings: 'OutfitMediaSettings',
} as const;

export type DoppeActionTypeWidgetItemListPredefinedMediaViewSettings = Values<typeof DoppeActionTypeWidgetItemListPredefinedMediaViewSettings>;

export const DoppeActionTypeWidgetItemList = reactCreateMemo(function DoppeActionTypeWidgetItemList<T>(props: DoppeActionTypeWidgetItemList.Props<T>) {
    const {
        dataSourceItems,
        layout,
        noPadding = false,
        listTitle,
        actionContext,
        onItemClick,
        renderItem,
        media,
        mediaViewSettings,
        mediaViewLayout,
        className,
        ...htmlAttributes
    } = props;

    const bymoPageContextProps = useBymoPageContextProps();

    const doppeActionTypeWidgetLayoutContext = useContext(DoppeActionTypeWidgetLayoutContext)
    const headerSpec = doppeActionTypeWidgetLayoutContext?.headerSpec ?? null;
    const isStrip = doppeActionTypeWidgetLayoutContext?.isStrip ?? false;

    const limit = doppePremiumFeatureGetLimit(
        bymoPageContextProps.premiumFeaturesStatus, DoppePremiumFeatures.hopp_add_items_to_list_action);



    const {
        ctaButtons,
        sections,
    } = actionContext.action.settings;


    const hasVisibleMultipleButtons = ctaButtons ? doppeActionCtaButtonsSettingsHasVisibleButton(actionContext.action.settings) : false;

    const normalizedProps = {
        layout,
        noPadding,
        listTitle,
        actionContext,
        onItemClick,
        renderItem,
        media,
        mediaViewSettings,
        mediaViewLayout,
        dataSourceItems: arraySkipTake(dataSourceItems, 0, limit)
    }

    return (
        <DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
            {...htmlAttributes}
            className={style(classes.root, {
                isImagesGrid: layout === DoppeActionLayout.ImagesGrid,
                isStrip: isStrip,
                hasHeader: headerSpec !== null,
                hasHeaderWithBottomBorder: headerSpec?.hasBorderBottom ?? false,
                hasOneItem: dataSourceItems.length === 1,
                noPadding: noPadding,
                hasTitle: listTitle !== undefined && doppeHideableValueIsVisibleAndNotEqualValue(listTitle, ''),
                hasSeeMoreButton: hasVisibleMultipleButtons,
            }, className)}
            sections={sections ?? []}
            buttons={hasVisibleMultipleButtons ? ctaButtons : []}
            actionContext={actionContext}
            reportGoalAchieved={(goalType) => {
                actionContext.reportGoalAchieved({
                    goalType: goalType,
                });
            }}
            noContentPadding={true}
            noContentSectionsPadding={true}
            mainMedia={evaluateFunction(() => {
                if (media) {

                    if (enumIsEnumValue(DoppeActionTypeWidgetItemListPredefinedMediaViewSettings, mediaViewSettings)) {
                        switch (mediaViewSettings) {
                            case DoppeActionTypeWidgetItemListPredefinedMediaViewSettings.OutfitMediaSettings: {
                                return (
                                    <DoppePageMediaView
                                        mediaResource={media}
                                        layoutSpec={{autoHeight: true, maxHeight: 360}}
                                        widthSpec={{contentRelativeWidth: 1}}
                                        mediaResizeMode={HtmlObjectResizeMode.Cover}
                                    />
                                )
                            }
                        }
                    } else {
                        return (
                            <DoppePageActionMediaView
                                mediaResource={media}
                                mediaViewSettings={mediaViewSettings}
                                widthSpec={{contentRelativeWidth: 1}}
                            />
                        )
                    }
                } else {
                    return null;
                }
            })}
            fullWidthMainMedia={evaluateFunction(() => {
                switch (mediaViewLayout) {
                    case BymoPageActionMediaViewLayout.FullWidth: {
                        return true;
                    }
                    case BymoPageActionMediaViewLayout.WithMargins: {
                        return false
                    }
                }
            })}
        >
            {evaluateFunction(() => {
                switch (layout) {
                    case DoppeActionLayout.Swiper: {
                        return (
                            <DoppeActionTypeWidgetItemListSwiper
                                {...normalizedProps}
                            />
                        )
                    }
                    case DoppeActionLayout.CarouselSmall:
                    case DoppeActionLayout.Carousel: {
                        return (
                            <DoppeActionTypeWidgetItemListCarousel
                                layout={layout}
                                onItemClick={onItemClick}
                                renderItem={renderItem}
                                dataSourceItems={dataSourceItems}
                                listTitle={listTitle}
                            />
                        )
                    }
                    case DoppeActionLayout.LargeColumn:
                    case DoppeActionLayout.Column:
                    case DoppeActionLayout.Grid:
                    case DoppeActionLayout.ImagesGrid: {
                        return (
                            <DoppeActionTypeWidgetItemListNormal
                                {...normalizedProps}
                            />
                        )
                    }
                }
            })}
        </DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons>
    )

});

export const DoppeActionTypeWidgetItemListCarousel = reactCreateMemo(function DoppeActionTypeWidgetItemListCarousel<T>(props: DoppeActionTypeWidgetItemList.CarouselProps<T>) {
    const {
        layout,
        onItemClick,
        renderItem,
        dataSourceItems,
        listTitle = doppeHideableValueCreateHidden(''),
        className,
        listClassName,
        ...htmlAttributes
    } = props;

    const scrollViewRef = useRef<ScrollView>(null);
    const isRtl = useDoppViewerIsRtl();
    const scrollbarSize = useWindowDependantMemo(() => domGetScrollbarSize(), []) ?? 0;

    return (
        <div
            {...htmlAttributes}
            className={style(classes.rootCaroussel, {}, className)}
        >
            <DoppeActionTypeWidgetInnerTitle
                className={style(classes.listTitle, {})}
            >
                {listTitle}
            </DoppeActionTypeWidgetInnerTitle>
            <ScrollView
                className={style(classes.itemsScrollView, FlexLayout.noShrink, FlexLayout.fillRemainingVerticalSpace)}
                ref={scrollViewRef}
                scrollingDirection={ScrollViewScrollingDirection.Horizontal}
                scrollbars={ScrollViewScrollbars.Hidden}
                // scrolledContentWrapperClassName={style(classes.contentListViewContainer)}
                fixedContent={scrollbarSize > 0 && dataSourceItems.length > 1 ? (
                    <>
                        <ScrollViewEdgeDisabled
                            scrollViewRef={scrollViewRef}
                            side={ScrollViewEdgeDisabledSide.Left}
                            className={style(classes.leftEdge, FlexLayout.centerContent)}
                            isRtl={isRtl}
                        >
                            {
                                (isDisabled, hasNoScrolling) => {
                                    return hasNoScrolling ? <div/> : <IconButton
                                        className={style(classes.arrowScrollButton, {isDisabled: isDisabled})}
                                        icon={!isRtl ? IconChevronLeft : IconChevronRight}
                                        onClick={() => {
                                            if (!isDisabled) {
                                                const scrollView = scrollViewRef.current;
                                                if (scrollView) {
                                                    scrollView.getScrolledContentElement().scrollBy({
                                                        behavior: 'smooth',
                                                        left: -scrollView.getScrollViewElement().offsetWidth / 2.63 * (isRtl ? -1 : 1)
                                                    })
                                                }
                                            }
                                        }}
                                    />
                                }
                            }
                        </ScrollViewEdgeDisabled>
                        <ScrollViewEdgeDisabled
                            scrollViewRef={scrollViewRef}
                            side={ScrollViewEdgeDisabledSide.Right}
                            className={style(classes.rightEdge, FlexLayout.centerContent)}
                            isRtl={isRtl}
                        >
                            {
                                (isDisabled, hasNoScrolling) => {
                                    return hasNoScrolling ? <div/> : <IconButton
                                        className={style(classes.arrowScrollButton, {isDisabled: isDisabled})}
                                        icon={!isRtl ? IconChevronRight : IconChevronLeft}
                                        onClick={() => {
                                            if (!isDisabled) {
                                                const scrollView = scrollViewRef.current;
                                                if (scrollView) {
                                                    scrollView.getScrolledContentElement().scrollBy({
                                                        behavior: 'smooth',
                                                        left: scrollView.getScrollViewElement().offsetWidth / 2.63 * (isRtl ? -1 : 1)
                                                    })
                                                }
                                            }
                                        }}
                                    />
                                }
                            }
                        </ScrollViewEdgeDisabled>
                    </>
                ) : null}
            >
                <ListView<T>
                    orientation={NavigationOrientation.Horizontal}
                    isFocusable={false}
                    className={style(classes.productsItemsListView, {
                        layout: layout,
                    }, listClassName)}
                    onItemClick={eventData => {
                        onItemClick(eventData.dataItem);
                    }}
                    renderItem={(renderProps) => {
                        return renderItem(renderProps.dataItem, layout, renderProps.itemIndex);
                    }}
                >
                    {dataSourceItems}
                </ListView>
            </ScrollView>
        </div>
    );
});

const DoppeActionTypeWidgetItemListNormal = reactCreateMemo(function DoppeActionTypeWidgetItemListNormal<T>(props: DoppeActionTypeWidgetItemList.PropsWithNoHtmlAttributes<T>) {
    const {
        layout,
        onItemClick,
        renderItem,
        dataSourceItems,
        listTitle = doppeHideableValueCreateHidden('')
    } = props;


    return (
        <div
            className={style(classes.listContent, {}, FlexLayout.column.default, FlexLayout.fillRemainingVerticalSpace)}
        >
            <DoppeActionTypeWidgetInnerTitle
                className={style(classes.listTitle, {})}
            >
                {listTitle}
            </DoppeActionTypeWidgetInnerTitle>
            <ListView<T>
                orientation={NavigationOrientation.Vertical}
                isFocusable={false}
                className={style(classes.productsItemsListView, {
                    layout: layout,
                }, FlexLayout.fillRemainingVerticalSpace)}
                onItemClick={eventData => {
                    onItemClick(eventData.dataItem);
                }}
                renderItem={(renderProps) => {
                    return renderItem(renderProps.dataItem, layout, renderProps.itemIndex);
                }}
            >
                {dataSourceItems}
            </ListView>
        </div>
    );
});


const DoppeActionTypeWidgetItemListSwiper = reactCreateMemo(function DoppeActionTypeWidgetItemListCarousel<T>(props: DoppeActionTypeWidgetItemList.PropsWithNoHtmlAttributes<T>) {
    const {
        layout,
        onItemClick,
        renderItem,
        dataSourceItems,
        listTitle = doppeHideableValueCreateHidden(''),
    } = props;

    const isRtl = useDoppViewerIsRtl();

    const swiperRef = useDynamicRef<HTMLDivElement>();
    const swiper = useSwiperNavigation(swiperRef);

    const scrollbarSize = useWindowDependantMemo(() => domGetScrollbarSize(), []) ?? 0;

    return (
        <>
            <div
                className={style(classes.swiperContainer, {}, FlexLayout.fillRemainingVerticalSpace)}
            >
                <DoppeActionTypeWidgetInnerTitle
                    className={style(classes.listTitle, {})}
                >
                    {listTitle}
                </DoppeActionTypeWidgetInnerTitle>
                <Swiper
                    ref={swiperRef}
                    visibleItemsCount={1}
                    isRtl={isRtl}
                    className={style(classes.productsItemsListView, {
                        layout: layout,
                    })}
                >
                    {
                        dataSourceItems.map((dataSourceItem, index) => {
                            return <div
                                className={style(classes.slide, {})}
                                key={dataSourceItem.id}
                                onClick={() => {
                                    onItemClick(dataSourceItem.dataItem);
                                }}
                            >
                                {renderItem(dataSourceItem.dataItem, layout, index)}
                            </div>
                        })
                    }
                </Swiper>
                {
                    scrollbarSize > 0 && swiper.currentSlide !== undefined && swiper.slidesCount > 1 ?
                        <>
                            <IconButton
                                className={style(classes.arrowScrollButton, {
                                    isDisabled: swiper.currentSlide === 0
                                }, classes.leftEdge)}
                                icon={isRtl ? IconChevronRight : IconChevronLeft}
                                onClick={() => {
                                    if (swiper.currentSlide !== undefined) {
                                        const targetElement = swiperRef.current?.children?.item(Math.max(swiper.currentSlide - 1, 0));

                                        if (targetElement) {
                                            swiperRef.current?.scrollTo({
                                                left: evaluateFunction(() => {
                                                    const previousSibling = targetElement.previousElementSibling as HTMLElement | null;
                                                    if (previousSibling) {
                                                        return previousSibling.offsetLeft + previousSibling.offsetWidth * (isRtl ? -1 : 1);
                                                    } else {
                                                        return 0;
                                                    }
                                                }),
                                                behavior: 'smooth'
                                            })
                                        }
                                    }

                                }}
                            />
                            <IconButton
                                className={style(classes.arrowScrollButton, {
                                    isDisabled: swiper.currentSlide === swiper.slidesCount - 1
                                }, classes.rightEdge)}
                                icon={!isRtl ? IconChevronRight : IconChevronLeft}
                                onClick={() => {
                                    if (swiper.currentSlide !== undefined) {
                                        const targetElement = swiperRef.current?.children?.item(Math.min(swiper.currentSlide + 1, swiper.slidesCount - 1));

                                        if (targetElement) {
                                            swiperRef.current?.scrollTo({
                                                left: evaluateFunction(() => {
                                                    const previousSibling = targetElement.previousElementSibling as HTMLElement | null;
                                                    if (previousSibling) {
                                                        return previousSibling.offsetLeft + previousSibling.offsetWidth * (isRtl ? -1 : 1);
                                                    } else {
                                                        return 0;
                                                    }
                                                }),
                                                behavior: 'smooth'
                                            })
                                        }
                                    }

                                }}
                            />
                        </>
                        :
                        null
                }
            </div>
            {
                swiper.slidesCount > 1 ?
                    <SwiperNavigationListView
                        className={style(classes.swiperNavigationListView, FlexLayout.row.alignToCenter)}
                        swiperElementRef={swiperRef}
                        isRtl={isRtl}
                        renderItem={(renderProps) => {
                            return (
                                <ListViewItemRoot
                                    className={style(classes.navigationMenuItem, {isActive: renderProps.isActive}, CommonClasses.button)}
                                />
                            )
                        }}
                    />
                    :
                    null
            }
        </>
    );
});


export namespace DoppeActionTypeWidgetItemList {

    export interface Props<T> extends React.HTMLAttributes<any> {
        layout: DoppeActionLayout;
        onItemClick: (item: T) => void;
        renderItem: (item: T, layout: DoppeActionLayout, index: number) => React.ReactElement;
        dataSourceItems: ListViewDataSourceDataItem<T>[];
        actionContext: IDoppeViewerActionContext;
        noPadding?: boolean;
        media?: WixMediaResource | null;
        mediaViewSettings: BymoPageActionMediaViewSettings | DoppeActionTypeWidgetItemListPredefinedMediaViewSettings;
        mediaViewLayout: BymoPageActionMediaViewLayout;
        listTitle?: DoppeHideableValue<string>,
    }


    export type CarouselProps<T> = React.HTMLAttributes<any> & {
        layout: DoppeActionLayout;
        onItemClick: (item: T) => void;
        renderItem: (item: T, layout: DoppeActionLayout, index: number) => React.ReactElement;
        dataSourceItems: ListViewDataSourceDataItem<T>[];
        listTitle?: DoppeHideableValue<string>,
        listClassName?: string;
    }

    export type PropsWithNoHtmlAttributes<T> = OmitStrict<DoppeActionTypeWidgetItemList.Props<T>, keyof React.HTMLAttributes<any>>;

}